type JoditTableModule = {
  getAllSelectedCells: () => HTMLTableCellElement[]
}

export type JoditInstanceForTableMultiCellStyleSync = {
  editor: HTMLElement
  o?: unknown
  getInstance?: <T>(name: string, options?: unknown) => T
}

const SYNC_STYLE_PROPS = [
  'backgroundColor',
  'borderColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'borderStyle',
  'borderTopStyle',
  'borderRightStyle',
  'borderBottomStyle',
  'borderLeftStyle',
  'borderWidth',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
] as const

function isTableCell(el: Node): el is HTMLTableCellElement {
  return el instanceof HTMLTableCellElement
}

export function registerJoditTableMultiCellStyleSync(editor: JoditInstanceForTableMultiCellStyleSync): () => void {
  const tableModule = editor.getInstance?.<JoditTableModule>('Table', editor.o)
  if (!tableModule) {
    return () => {
      return
    }
  }

  let isSyncing = false

  const syncFromSourceCell = (sourceCell: HTMLTableCellElement): void => {
    const selectedCells = tableModule.getAllSelectedCells()
    if (selectedCells.length < 2 || !selectedCells.includes(sourceCell)) {
      return
    }

    isSyncing = true
    try {
      selectedCells.forEach(cell => {
        if (cell === sourceCell) {
          return
        }

        SYNC_STYLE_PROPS.forEach((propName) => {
          const sourceValue = sourceCell.style[propName]
          const currentValue = cell.style[propName]

          if (sourceValue !== currentValue) {
            // Empty source means the inline style was cleared and should be cleared in all selected cells.
            const cssProp = propName.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)
            if (sourceValue) {
              cell.style.setProperty(cssProp, sourceValue)
            } else {
              cell.style.removeProperty(cssProp)
            }
          }
        })
      })
    } finally {
      isSyncing = false
    }
  }

  const observer = new MutationObserver((mutations) => {
    if (isSyncing) {
      return
    }

    mutations.forEach((mutation) => {
      if (
        mutation.type !== 'attributes'
        || mutation.attributeName !== 'style'
        || !isTableCell(mutation.target)
      ) {
        return
      }

      syncFromSourceCell(mutation.target)
    })
  })

  observer.observe(editor.editor, {
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  })

  return () => {
    try {
      observer.disconnect()
    } catch {
      /* ignore */
    }
  }
}
